/**
 * ListRedirectPanel — top-level /lists/:listId entry that resolves
 * the list's parent context (work/project) and redirects to the
 * canonical nested URL.
 *
 * Used by deep links (e.g. inheritance Outgoing list) where only
 * the list id is known.
 */

import { Panel, type RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { useStackNavigate } from '@/app/navigation/stack-links';
import { useChecklist } from '@/features/lists/lists.hooks';
import { useWork } from '@/features/works/works.hooks';
import { useEffect } from 'react';

export function ListRedirectPanel({ params, ...panelProps }: RoutedPanelProps) {
  const { listId } = params;
  const go = useStackNavigate();
  const { data: list } = useChecklist(listId, { suspense: false });
  const { data: work } = useWork(list?.werk_id ?? undefined, {});

  useEffect(() => {
    if (!list) return;

    // Template (no werk_id) → templates section
    if (list.werk_id == null) {
      go({ to: `/templates/${listId}`, replace: true });
      return;
    }

    // Checklist needs project + work context
    if (work) {
      go({
        to: `/projects/${work.project_id}/works/${work.recnum}/lists/${listId}`,
        replace: true,
      });
    }
  }, [go, list, work, listId]);

  return <Panel {...panelProps}>redirecting…</Panel>;
}
